import * as React from 'react';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';
import DateTimeInput from '../../components/DateTimeInput';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import SingleSelect from '../../components/SingleSelect';
import FileUpload from '../../components/FileUpload';

// ICONS
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';

// TYPES
import { ComplaintsFormDataTypes } from './ComplaintsSubmit';
import { DomainProfileType } from '../../api/models';

interface StepOneTypes {
	complaintsFormData: ComplaintsFormDataTypes;
	setComplaintsFormData: React.Dispatch<React.SetStateAction<ComplaintsFormDataTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
}

const StepOne: React.FC<StepOneTypes> = ({ complaintsFormData, setComplaintsFormData, setActiveStep }) => {
	// Check Profile Type of the Active User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// Get all Complaint Categories from Redux store
	const complaintCategories = useAppSelector((state) => state.lists.complaintCategories);

	// Submit Step One Function
	const onSubmitStepOne = (values: any) => {
		setActiveStep('step2');

		window.scrollTo(0, 0);

		setComplaintsFormData((prevState) => ({
			...prevState,

			loggingType: values.loggingTypeInput,
			loggingOnBehalf: values.loggingOnBehalfInput,
			anonymousContact: values.anonymousContactInput,
			category: values.categoryInput,
			subject: values.subjectInput,
			occurrence: values.occurrenceInput,
			authority: values.authorityInput,
			location: values.locationInput,
			accused: values.accusedInput,
			accusedRelationship: values.accusedRelationshipInput,
			impacted: values.impactedInput,
			impactedRelationship: values.impactedRelationshipInput,
			othersInvolved: values.othersInvolvedInput,
			witnesses: values.witnessesInput,
			isInitialComplaint: values.isFirstComplaintInput,
			description: values.descriptionInput,
			comments: values.commentsInput,
			createAttachments: values.createAttachments,
			attachments: values.attachments
		}));
	};

	const validationSchemaStepOne = Yup.object({
		loggingTypeInput: Yup.string().required('Required'),
		loggingOnBehalfInput: Yup.string().when('loggingTypeInput', {
			is: 'SomeoneElse',
			then: Yup.string().required('Required')
		}),
		categoryInput: Yup.string().required('Required'),
		subjectInput: Yup.string().required('Required'),
		occurrenceInput: Yup.string().required('Required'),
		locationInput: Yup.string().required('Required'),
		accusedInput: Yup.string().required('Required'),
		accusedRelationshipInput: Yup.string().required('Required'),
		impactedInput: Yup.string().required('Required'),
		impactedRelationshipInput: Yup.string().required('Required'),
		othersInvolvedInput: Yup.string().required('Required'),
		witnessesInput: Yup.string().required('Required'),
		isFirstComplaintInput: Yup.boolean().required('Required'),
		descriptionInput: Yup.string().required('Required'),
		commentsInput: Yup.string().required('Required')
	});

	const initialValuesStepOne = {
		loggingTypeInput: complaintsFormData.loggingType || '',
		loggingOnBehalfInput: complaintsFormData.loggingOnBehalf || '',
		anonymousContactInput: complaintsFormData.anonymousContact || '',
		categoryInput: complaintsFormData.category || '',
		subjectInput: complaintsFormData.subject || '',
		occurrenceInput: complaintsFormData.occurrence || '',
		authorityInput: complaintsFormData.authority || '',
		locationInput: complaintsFormData.location || '',
		accusedInput: complaintsFormData.accused || '',
		accusedRelationshipInput: complaintsFormData.accusedRelationship || '',
		impactedInput: complaintsFormData.impacted || '',
		impactedRelationshipInput: complaintsFormData.impacted || '',
		othersInvolvedInput: complaintsFormData.othersInvolved || '',
		witnessesInput: complaintsFormData.witnesses || '',
		isFirstComplaintInput: complaintsFormData.isInitialComplaint || false,
		descriptionInput: complaintsFormData.description || '',
		commentsInput: complaintsFormData.comments || '',
		createAttachments: complaintsFormData.createAttachments,
		attachments: complaintsFormData.attachments
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validationSchema={validationSchemaStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main">
								<div className="form-group">
									<div className="form-field">
										<div className="radio-row">
											<Field
												name="loggingTypeInput"
												id="loggingTypeInput"
												type="radio"
												value="Myself"
												checked={formik.values.loggingTypeInput === 'Myself'}
												onChange={() => formik.setFieldValue('loggingTypeInput', 'Myself')}
											/>

											<span className="radio-option">I am making a complaint</span>
										</div>

										{(activeProfileType === DomainProfileType.SuperAdministrator ||
											activeProfileType === DomainProfileType.Staff) && (
											<>
												<div className="radio-row">
													<Field
														name="loggingTypeInput"
														id="loggingTypeInput"
														type="radio"
														value="SomeoneElse"
														checked={formik.values.loggingTypeInput === 'SomeoneElse'}
														onChange={() => formik.setFieldValue('loggingTypeInput', 'SomeoneElse')}
													/>

													<span className="radio-option">
														I am recording a complaint received by the organisation (e.g. phone call, email, letter)
													</span>
												</div>

												{formik.values.loggingTypeInput === 'SomeoneElse' && (
													<div className="form-field">
														<Field
															name="loggingOnBehalfInput"
															id="loggingOnBehalfInput"
															type="text"
															className="form-input sub-input"
															placeholder="Please provide their name"
														/>

														<ErrorMessage
															name="loggingOnBehalfInput"
															component="div"
															className="error-message sub-input"
														/>
													</div>
												)}
											</>
										)}

										<div className="radio-row">
											<Field
												name="loggingTypeInput"
												id="loggingTypeInput"
												type="radio"
												value="Anonymous"
												checked={formik.values.loggingTypeInput === 'Anonymous'}
												onChange={() => formik.setFieldValue('loggingTypeInput', 'Anonymous')}
											/>

											<span className="radio-option">Anonymously</span>
										</div>

										{formik.values.loggingTypeInput === 'Anonymous' && (
											<div className="form-field">
												<Field
													name="anonymousContactInput"
													id="anonymousContactInput"
													type="text"
													className="form-input sub-input"
													placeholder="Contact email or phone number (optional)"
												/>

												<ErrorMessage
													name="anonymousContactInput"
													component="div"
													className="error-message sub-input"
												/>
											</div>
										)}

										<ErrorMessage name="loggingTypeInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="categoryInput" className="label">
										Complaint Type
									</label>

									<SingleSelect
										options={complaintCategories}
										name="categoryInput"
										value={formik.values.categoryInput}
										onChange={(val: string) => formik.setFieldValue('categoryInput', val)}
									/>

									<ErrorMessage name="categoryInput" component="div" className="error-message" />
								</div>

								<div className="form-group">
									<label htmlFor="subjectInput" className="label">
										Subject
									</label>

									<div className="form-field">
										<Field
											name="subjectInput"
											placeholder="eg. persistent bullying by another child"
											id="subjectInput"
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="subjectInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Date and time of relevant event</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrenceInput || ''}
										onChange={(val: string) => formik.setFieldValue('occurrenceInput', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="locationInput" className="label">
										Location of relevant event
									</label>

									<div className="form-field">
										<Field name="locationInput" id="locationInput" type="text" className="form-input" />

										<ErrorMessage name="locationInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="accusedInput" className="label">
										Who is the complaint about?
									</label>

									<div className="form-field">
										<Field name="accusedInput" id="accusedInput" type="text" className="form-input" />

										<ErrorMessage name="accusedInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="accusedRelationshipInput" className="label">
										What is your relationship to this person(s)?
									</label>

									<div className="form-field">
										<Field
											name="accusedRelationshipInput"
											id="accusedRelationshipInput"
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="accusedRelationshipInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="impactedInput" className="label">
										Name of person(s) impacted
									</label>

									<div className="form-field">
										<Field name="impactedInput" id="impactedInput" type="text" className="form-input" />

										<ErrorMessage name="impactedInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="impactedRelationshipInput" className="label">
										What is your relationship to this person(s)?
									</label>

									<div className="form-field">
										<Field
											name="impactedRelationshipInput"
											id="impactedRelationshipInput"
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="impactedRelationshipInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="othersInvolvedInput" className="label">
										Who else was involved in the relevant event?
									</label>

									<div className="form-field">
										<Field name="othersInvolvedInput" id="othersInvolvedInput" type="text" className="form-input" />

										<ErrorMessage name="othersInvolvedInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="witnessesInput" className="label">
										Who witnessed the relevant event?
									</label>

									<div className="form-field">
										<Field name="witnessesInput" id="witnessesInput" type="text" className="form-input" />

										<ErrorMessage name="witnessesInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Is this your first complaint about this matter?</label>

									<div className="form-field">
										<div className="radio-row">
											<Field
												name="isFirstComplaintInput"
												id="isFirstComplaintInput"
												type="radio"
												value={true}
												checked={formik.values.isFirstComplaintInput === true}
												onChange={() => formik.setFieldValue('isFirstComplaintInput', true)}
											/>
											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row">
											<Field
												name="isFirstComplaintInput"
												id="isFirstComplaintInput"
												type="radio"
												value={false}
												checked={formik.values.isFirstComplaintInput === false}
												onChange={() => formik.setFieldValue('isFirstComplaintInput', false)}
											/>
											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="isFirstComplaintInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="descriptionInput" className="label">
										Please detail your complaint including details of relevant event/s.
									</label>

									<div className="form-field">
										<Field
											as="textarea"
											name="descriptionInput"
											id="descriptionInput"
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="descriptionInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="authorityInput" className="label">
										Was an authority notified? <br />
										If so, please provide the name of the authority and the contact details of the person that you spoke
										to at that authority
									</label>

									<div className="form-field">
										<Field name="authorityInput" id="authorityInput" type="text" className="form-input" />

										<ErrorMessage name="authorityInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="commentsInput" className="label">
										Are there any additional comments?
									</label>

									<div className="form-field">
										<Field as="textarea" name="commentsInput" id="commentsInput" type="text" className="form-input" />

										<ErrorMessage name="commentsInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="commentsInput" className="label">
										Add any supporting documents or images?
									</label>

									<div className="form-field mbtom-14">
										<FileUpload
											name="createAttachments"
											maxSizeMb={5}
											multiple={true}
											onChange={(vals: string[], files: any[]) => {
												formik.setFieldValue('createAttachments', vals);
												formik.setFieldValue('attachments', files);
											}}
											files={formik.values.attachments}
										/>
									</div>
								</div>
							</div>

							<div className="general-form-guide">
								<QuestionMarkIcon />

								<h2 className="guide-heading">When to submit a complaint</h2>

								<p className="guide-content">
									Complaints are the most serious way for individuals to voice their safety concerns. Complaints about
									children’s safety must be taken seriously, investigated thoroughly, and actioned appropriately. A
									ChildAware organisation will have a comprehensive, child-focused complaints handling policy available
									to staff, parents, children, volunteers and visitors that will guide how complaints are to be handled.
									ChildAware organisations recognise that complaints present an opportunity for complainants to have
									their concerns addressed and for organisations to continually improve child safety practices.
								</p>

								<p className="guide-content">
									If you have a general suggestion or comment about how an organisation might improve children’s safety
									you may wish to submit&nbsp;
									<Link className="link" to="/feedback">
										Feedback
									</Link>
									&nbsp;instead.
								</p>

								<p className="guide-content">
									<strong>Note:</strong> If a child is in immediate danger, call police or emergency services on 000.
								</p>
							</div>
						</div>

						<ScrollToTopButton offsetValue={150} />

						<div className="general-divider"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepOne;
